/* import __COLOCATED_TEMPLATE__ from './dialog-modal.hbs'; */
/**

## Modal

A modal is a box-like element that appears over the rest of the page and UI to display some UI that
the user should focus on

### Contextual Components

| Description | Contextual Component |
| --- | --- |
| Header | [dialog-header](./dialog-header.html) |
| Header / Title | [dialog-header/title](./dialog-header/title.html) |
| Header / Subheader | [dialog-header/subheader](./dialog-header/subheader.html) |
| Body | [dialog-body](./dialog-body.html) |
| Footer | [dialog-actions](./dialog-actions.html) |
| Footer / Button (Primary/Secondary) | [dialog-actions/button](./dialog-actions/button.html) |
| Secondary Actions | [dialog-actions](./dialog-actions.html) |
| Actions / Secondary Actions -> Button | [dialog-actions/button](./dialog-actions/button.html) |
| Actions / Secondary Actions -> Button Close X | [dialog-actions/button-close-x](./dialog-actions/button-close-x.html) |
| Actions / Secondary Actions -> Icon Close X | [svg/icon-close-x](./svg/icon-close-x.html) |
| Actions / Secondary Actions -> Button Back Arrow | [dialog-actions/button-back-arrow](./dialog-actions/button-back-arrow.html) |

#### Check out the [adding modals guide](/glass/docs/development/adding-modals.html) to see how to show a modal.

```example
@title Basic Modal Example

@controller-properties
{
  flash: Ember.inject.service(),
  showModal: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Modal" show=showModal}}
  {{!  this `modal-layout` class is a visual hack for fuse-docs, it's not needed for your modal }}
  <div class="modal-layout">
    {{#dialog-modal title="Modal Title" as |modal|}}
      {{#modal.body}}
        <p>I am a paragraph of modal content</p>
        {{! all your content for the Modal goes here }}
      {{/modal.body}}

      {{#modal.footer as |footer|}}
        {{footer.button-secondary label="Cancel" click=(action "close")}}
        {{footer.button-primary label="Ok" click=(action "close")}}
      {{/modal.footer}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```

```example
@title Modal without Dividers

@controller-properties
{
  showModal: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Modal without Dividers" show=showModal}}
  {{!  this `modal-layout` class is a visual hack for fuse-docs, it's not needed for your modal }}
  <div class="modal-layout">
    {{#dialog-modal title="Modal Title" hasDividers=false as |modal|}}
      {{#modal.body}}
        <p>I am a paragraph of modal content</p>
        {{! all your content for the Modal goes here }}
      {{/modal.body}}

      {{#modal.footer as |footer|}}
        {{footer.button-secondary label="Cancel" click=(action "close")}}
        {{footer.button-primary label="Ok" click=(action "close")}}
      {{/modal.footer}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```


```example
@title Customizing the Title
@description <strong>This is highly discouraged and should be rarely needed. 98% of the time, you can just
pass a string to the <code>title</code> param.</strong> You can pass a <code>{{#modal.title}}</code> block if you need to pass formatted HTML
to the title area for some reason. Make sure to remove the <code>title</code> param from the parent <code>dialog-modal</code>
component as this param will override anything you pass to the block.

@controller-properties
{
  showModal: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Modal" show=showModal}}
  {{!  this `modal-layout` class is a visual hack for fuse-docs, it's not needed for your modal }}
  <div class="modal-layout">
    {{#dialog-modal as |modal|}}
      {{#modal.title}}
        <span class="online-status-indicator online-status-indicator--online">Online</span> Taylor Swift
      {{/modal.title}}
      {{#modal.body}}
        <p>I am a paragraph of modal content</p>
        {{! all your content for the Modal goes here }}
      {{/modal.body}}

      {{#modal.footer as |footer|}}
        {{footer.button-secondary label="Cancel" click=(action "close")}}
        {{footer.button-primary label="Ok" click=(action "close")}}
      {{/modal.footer}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```

```example
@title Custom Secondary Actions
@description You can customize the secondary actions (aka the ones on the left of the title) using the `secondary-actions` contextual component.

@controller-properties
{
  showBackArrow: true,
  showSheet: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
 {{#toggle-code-example exampleName="Modal with custom secondary actions" show=showModal}}
  {{!  this `modal-layout` class is a visual hack for fuse-docs, it's not needed for your modal }}
  <div class="modal-layout">
    {{#dialog-modal title="Basic Modal"
                    hasSecondaryActionsBlock=true
                    as |modal|}}
      {{#modal.secondary-actions as |actions|}}
        {{#if showBackArrow}}
          {{actions.button-back-arrow click=(action (mut showBackArrow) false)}}
        {{else}}
          {{actions.button-close-x click=(action "close")}}
        {{/if}}
      {{/modal.secondary-actions}}

      {{#modal.body}}
        <p><strong>showBackArrow:</strong> {{showBackArrow}}</p>
        {{#if showBackArrow}}
          <p>Click above to change the secondary action!</p>
        {{else}}
          <p>Click below to change the secondary action!</p>
        {{/if}}
      {{/modal.body}}

      {{#modal.footer as |footer|}}
        {{#if showBackArrow}}
          {{footer.button-primary label="Done"
                                  click=(action "close")}}
        {{else}}
          {{footer.button-primary label="Continue"
                                  click=(action (mut showBackArrow) true)}}
        {{/if}}
      {{/modal.footer}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```

```example
@title Single Section modal

@controller-properties
{
  showModal: false,
  actions: {
    close() {
      this.set('showModal', false);
    }
  }
}

@hbs
{{#toggle-code-example exampleName="Modal with dialogLayout=SINGLE_SECTION" show=showModal}}
  {{!  this `modal-layout` class is a visual hack for fuse-docs, it's not needed for your modal }}
  <div class="modal-layout">
    {{#dialog-modal title="Modal Title" as |modal|}}
      {{#modal.body}}
        <p>This should only be used in rare circumstances, when we're presenting information that
           the user must acknowledge but there is no immediate choice to act on.</p>
      {{/modal.body}}
    {{/dialog-modal}}
  </div>
{{/toggle-code-example}}
```

#### Check out the [Flash guide](/glass/docs/development/flash.html) to see how to show a flash message within a modal.

### Params
@param {Boolean} [hasDividers=true] - If true, shows lines above and below the content. Note that dividers always appear if the inner content scrolls (applied to dialog-modal)
@param {Boolean} [hasSecondaryActionsBlock=false] - If true, replaces the default close icon and action with a yielded `secondary-actions` contextual component block to customize the secondary/icon actions however you please (as shown in the "Custom Secondary Actions" code example above). Make sure *not* to pass this parameter if you are not yielding a secondary actions block, because otherwise no actions will be rendered in the header.
@param {String} [dialogLayout='DEFAULT'] - DEFAULT or SINGLE_SECTION. Single section modals omit the header, footer, and close button, allowing only for horizontally and vertically centered body content.
@param {String} [title] - Localized string (applied to dialog-modal). Required unless dialogLayout === 'SINGLE_SECTION'.
@param {String} [autofocusSelector] - If provided, autofocuses the actionable element found by the selector in the standard dialog. Otherwise the first actionable element is focused.
@param {FlashContext} [flashContext] - If a flash context is passed in, it will be used instead of the modal context from the flash service

Style dependencies at: <code>@square/glass-ui/addon/styles/@square/dialog/modal</code>

*/

import Component from '@ember/component';
import { debounce } from '@ember/runloop';
import { reads, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { CLOSE_X } from '../services/modal';
import { isActionableElement } from '../services/focus';
const KEY_ENTER = 13;
export const MODAL_LAYOUT = {
  SINGLE_SECTION: 'SINGLE_SECTION',
  DEFAULT: 'DEFAULT'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  attributeBindings: ['data-test-modal'],
  'data-test-modal': readOnly('title'),
  autofocusSelector: null,
  hasDividers: true,
  hasSecondaryActionsBlock: false,
  title: null,
  classNames: ['dialog', 'dialog--modal'],
  classNameBindings: ['isShowingDividers:dialog--modal--with-dividers'],
  ariaRole: 'dialog',
  dialogLayout: MODAL_LAYOUT.DEFAULT,
  // internal use only: reads from the public API but can be changed
  isShowingDividers: reads('hasDividers'),
  modalService: service('modal'),
  flash: service(),
  didInsertElement(...args) {
    this._super(...args);

    // add a resize event handler to force dividers if the modal content scrolls
    if (!this.get('hasDividers')) {
      this._windowEventListener = () => debounce(this, this._forceDividersWhenContentScrolls, 50);
      window.addEventListener('resize', this._windowEventListener);
    }
  },
  didRender(...args) {
    this._super(...args);
    if (!this.get('hasDividers')) {
      this._forceDividersWhenContentScrolls();
    }
  },
  didDestroyElement(...args) {
    this._super(...args);
    window.removeEventListener('resize', this._windowEventListener);
  },
  keyDown(e) {
    if (e.keyCode === KEY_ENTER && !isActionableElement(e.target) && this.onEnterKeyDown) {
      this.onEnterKeyDown(e);
    }
  },
  _forceDividersWhenContentScrolls() {
    if (this.get('isDestroyed')) {
      return;
    }
    const contentElement = this.element.querySelector('.dialog--modal__content');
    if (!contentElement) {
      return;
    }
    const height = contentElement.offsetHeight;
    const scrollHeight = contentElement.scrollHeight;

    // something about the fact that padding changes makes for
    // weird rounding issues. Allowing a difference of 1 removes
    // some visual glitching.
    if (scrollHeight - height > 1) {
      this.set('isShowingDividers', true);
    } else if (!this.get('hasDividers')) {
      this.set('isShowingDividers', false);
    }
  },
  actions: {
    close() {
      this.get('modalService').maybeCloseOnEvent(CLOSE_X);
    }
  }
});